import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverConfig() {
  const { api, AdminId } = useGlobalContext();

  const [configRows, setConfigRows] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const normalizeConfigForm = (row) => ({
    cashLimit: row?.cash_limit?.toString?.() ?? "",
    dailyTaskLimit: row?.daily_task_limit?.toString?.() ?? "",
    concurrentOrder: row?.concurrent_order?.toString?.() ?? "",
    maxTeamLimit: row?.max_team_limit?.toString?.() ?? "",
  });

  /* ================= GET ALL DRIVER CONFIGS ================= */

  const getDriverConfigApi = useCallback(
    async (driverCode = "") => {
      try {
        const res = await api.post(
          "/api/Driver/v1.0/getAllDriverConfigDetails",
          {
            adminId: AdminId,
            driverCode,
          }
        );

        if (res.data?.responseCode === "00") {
          return res.data?.data || [];
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(res.data?.responseMessage || "Failed to fetch config");
        }

        return [];
      } catch (error) {
        console.error("getDriverConfigApi error:", error);
        toast.error("Error fetching config");
        return [];
      }
    },
    [api, AdminId]
  );

  const fetchAllDriverConfigs = useCallback(
    async (silent = false) => {
      if (!silent) setConfigLoading(true);

      try {
        const data = await getDriverConfigApi("");
        setConfigRows(data);
        return data;
      } catch (error) {
        console.error("fetchAllDriverConfigs error:", error);
        setConfigRows([]);
        return [];
      } finally {
        if (!silent) setConfigLoading(false);
      }
    },
    [getDriverConfigApi]
  );

  /* ================= GET CONFIG BY DRIVER CODE ================= */

  const fetchDriverConfigByCode = async (driverCode, setFetching, setConfig) => {
    setFetching(true);

    try {
      const data = await getDriverConfigApi(driverCode);
      const row = data.find((item) => item.driver_code === driverCode) || null;

      setConfig(row);
      return row;
    } finally {
      setFetching(false);
    }
  };

  /* ================= QUICK ADD CONFIG ================= */

  const fetchDriverConfigForQuickAdd = async (
    driverCode,
    setFetching,
    setExistingConfig,
    setForm
  ) => {
    setFetching(true);

    try {
      const data = await getDriverConfigApi(driverCode);
      const row = data.find((item) => item.driver_code === driverCode) || null;

      setExistingConfig(row);
      setForm(normalizeConfigForm(row));

      return row;
    } finally {
      setFetching(false);
    }
  };

  /* ================= SAVE CONFIG ================= */

  const saveDriverConfig = async (payload) => {
    if (!payload) {
      toast.error("Missing config payload");
      return false;
    }

    try {
      const res = await api.post(
        "/api/Driver/v1.0/insertDriverConfigDetails",
        payload
      );

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Saved");
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to save");
      }

      return false;
    } catch (error) {
      console.error("saveDriverConfig error:", error);
      toast.error("Error saving config");
      return false;
    }
  };

  const saveDriverQuickConfig = saveDriverConfig;

  /* ================= SINGLE DRIVER MODAL CONFIG ================= */

  const fetchSingleDriverConfig = async (
    driverCode,
    setFetching,
    setExistingConfig,
    setForm
  ) => {
    if (!driverCode) return null;

    setFetching(true);

    try {
      const data = await getDriverConfigApi(driverCode);
      const row = data.find((item) => item.driver_code === driverCode) || null;

      setExistingConfig(row);
      setForm(normalizeConfigForm(row));

      return row;
    } finally {
      setFetching(false);
    }
  };

  /* ================= DELETE CONFIG ================= */

  const deleteDriverConfig = async (configId) => {
    if (!configId) {
      toast.error("Invalid config ID");
      return false;
    }

    try {
      const res = await api.post(
        "/api/Driver/v1.0/deleteDriverConfigDetails",
        {
          adminId: AdminId,
          configId: String(configId),
        }
      );

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Deleted");
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Delete failed");
      }

      return false;
    } catch (error) {
      console.error("deleteDriverConfig error:", error);
      toast.error("Error deleting config");
      return false;
    }
  };

  const deleteDriverQuickConfig = deleteDriverConfig;

  const handleDeleteConfig = async (configId) => {
    const ok = await deleteDriverConfig(configId);

    if (ok) {
      await fetchAllDriverConfigs(true);
    }

    return ok;
  };

  return {
    configRows,
    configLoading,
    fetchAllDriverConfigs,
    fetchDriverConfigByCode,
    fetchDriverConfigForQuickAdd,
    saveDriverConfig,
    saveDriverQuickConfig,
    saveSingleDriverConfig: saveDriverConfig,
    fetchSingleDriverConfig,
    deleteDriverConfig,
    deleteDriverQuickConfig,
    handleDeleteConfig,
    deleteSingleDriverConfig: deleteDriverConfig,
  };
}